import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import type { Device } from 'src/types/Device';
import { isCameraDevice } from 'src/helpers/interface_helpers';
import { getBatteryIcon, getBatteryIconStyle } from 'src/helpers/device_data_helpers';
import { camera_cloud_auth } from 'src/components/Camera/stores/camera_cloud_auth';
import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
import { camera_storage_resolved_keys } from 'src/components/Camera/stores/camera_storage_resolved_keys';
import { camera_storage_passphrases } from 'src/components/Camera/stores/camera_storage_passphrases';
import { camera_key_prompt } from 'src/components/Camera/stores/camera_key_prompt';
import { camera_setup_prompt } from 'src/components/Camera/stores/camera_setup_prompt';
import type { CameraRecording, CameraRecordings } from '../types/camera-status';
import { getCameraMonitoringSettings } from './camera-monitoring-settings';

const _format = get(format);

/**
 * Build the `camera:{channel}` component key for a device.
 * Falls back to `camera:0` when the device has no channel set.
 */
export const getCameraComponentKey = (device?: Device | null): string => {
	return `camera:${device?.channel || 0}`;
};

/**
 * Whether motion detection is armed, from `status['camera:{n}'].arm`.
 *
 * @param settings - LEGACY_CAMERA_ARM_PRIVACY: fallback for firmware that still reports
 * `arm` in the camera config. Drop the parameter once no supported firmware does.
 */
export const isCameraArmed = (
	device: Device | null | undefined,
	status: DeviceStatusG2 | undefined,
	settings: DeviceSettingsG2 | undefined,
): boolean => {
	const cameraKey = getCameraComponentKey(device);
	return status?.[cameraKey]?.arm ?? settings?.[cameraKey]?.arm ?? false;
};

/**
 * Whether privacy mode is on, from `status['camera:{n}'].privacy`. While on, the camera
 * serves no stream, so callers use this to block the player.
 *
 * @param settings - LEGACY_CAMERA_ARM_PRIVACY: fallback for firmware that still reports
 * `privacy` in the camera config. Drop the parameter once no supported firmware does.
 */
export const isCameraPrivacyEnabled = (
	device: Device | null | undefined,
	status: DeviceStatusG2 | undefined,
	settings: DeviceSettingsG2 | undefined,
): boolean => {
	const cameraKey = getCameraComponentKey(device);
	return status?.[cameraKey]?.privacy ?? settings?.[cameraKey]?.privacy ?? false;
};

export const getCameraStatusLabelList = (
	device: Device,
	device_status: DeviceStatusG2,
	device_settings: DeviceSettingsG2,
) => {
	const cameraKey = getCameraComponentKey(device);
	let hasMotion = !!device_status?.[cameraKey]?.motion;
	let statusLabels: {
		name: string;
		icon: string;
		value: string;
		iconStyle?: string;
		iconColor?: string;
	}[] = [
		{
			name: 'motion',
			icon: hasMotion ? 'text-primary fa fa-person-walking' : 'fas fa-person',
			value: hasMotion
				? _format('motion', { default: 'Motion' })
				: _format('status_no', { default: 'No' }),
		},
	];

	const devicePower = device_status?.['devicepower:0'];
	if (typeof devicePower?.battery !== 'undefined') {
		const batteryPercent = devicePower?.battery?.percent;
		statusLabels.push({
			name: 'battery',
			icon: getBatteryIcon(batteryPercent),
			value:
				batteryPercent != null
					? batteryPercent + ' %'
					: _format('not_available_short', { default: 'N/A' }),
			iconStyle: getBatteryIconStyle(batteryPercent),
		});
	}

	return statusLabels;
};

export const getCameraDevices = (devices: Record<string, Device>): Device[] => {
	return Object.values(devices || {}).filter((device) => isCameraDevice(device)) as Device[];
};

export const hasCameraDevices = (devices: Record<string, Device>): boolean => {
	return getCameraDevices(devices).length > 0;
};

/**
 * Count for the Camera Monitoring dashboard card: configured cameras when set,
 * otherwise all camera devices on the account.
 */
export function getCameraMonitoringCount(homeData: unknown, devices: Record<string, Device>): number {
	const configuredCount = getCameraMonitoringSettings(homeData)?.cameras?.length ?? 0;
	if (configuredCount > 0) return configuredCount;
	return getCameraDevices(devices ?? {})?.length ?? 0;
}

/**
 * Clear all camera-related stores (auth, encrypted key backups, and resolved
 * master keys). Call this on logout, account switch, or profile switch. Latest
 * recording thumbnails need no clearing: they live in device status.
 *
 * @remarks
 * Dropping the key backups and resolved keys here is safe on an account switch:
 * backups are re-fetched from the active account's cloud (`loadCameraStorageKeys`)
 * and the plaintext `master_key` is re-derived on demand from the preserved
 * per-account passphrase. The account-scoped passphrases are intentionally NOT
 * cleared here so a returning account never has to re-enter its password; they are
 * only cleared on a real logout (see `handleLogout`).
 */
export function clearCameraStores(): void {
	console.log('[camera-helpers] Clearing all camera stores');
	camera_cloud_auth.clear();
	camera_storage_keys.clear();
	camera_storage_resolved_keys.clear();
}

/**
 * Forget a user's preserved camera credentials. Call this only on a REAL logout
 * (not an account/profile switch): it drops the account-scoped recording
 * passphrases and resets the once-per-session key-prompt memory, so the next
 * login must re-enter its password and gets a fresh unlock prompt.
 *
 * @remarks
 * This is the counterpart to {@link clearCameraStores}: that soft clear is safe
 * on an account switch and deliberately keeps passphrases so a returning account
 * never has to re-enter its password, whereas this hard clear removes them.
 * Resetting `camera_key_prompt` and `camera_setup_prompt` clears the once-per-session
 * prompt memory for every device (neither is account-scoped), which is intended on logout.
 *
 * @param email - Normalised account email to forget; when omitted, every
 * account's passphrases are cleared (used by the logout-all path).
 */
export function clearCameraCredentials(email?: string): void {
	if (email) {
		camera_storage_passphrases.removeAccount(email);
	} else {
		camera_storage_passphrases.clear();
	}
	camera_key_prompt.reset();
	camera_setup_prompt.reset();
}

export function isCameraOnline(cameraStatus: DeviceStatusG2): boolean {
	return cameraStatus?.online || false;
}

/**
 * Enter native fullscreen for a camera `<video>` element (incl. iOS WebKit fallbacks).
 */
export function requestVideoFullscreen(videoElement: HTMLVideoElement | null | undefined): boolean {
	if (!videoElement) return false;

	if (videoElement.requestFullscreen) {
		videoElement.requestFullscreen();
		return true;
	}

	const webkitVideo = videoElement as HTMLVideoElement & {
		webkitEnterFullscreen?: () => void;
		webkitRequestFullscreen?: () => void;
	};

	if (webkitVideo.webkitEnterFullscreen) {
		webkitVideo.webkitEnterFullscreen();
		return true;
	}

	if (webkitVideo.webkitRequestFullscreen) {
		webkitVideo.webkitRequestFullscreen();
		return true;
	}

	return false;
}

/**
 * Find the most recent active recording on stream 0 that has a duration.
 */
export function getActiveStreamRecording(
	recordings: CameraRecordings | undefined,
): CameraRecording | null {
	if (!recordings) return null;

	let latest: CameraRecording | null = null;

	for (const [id, rec] of Object.entries(recordings)) {
		if (rec.stream === 0 && rec.duration > 0) {
			if (!latest || rec.ts > latest.ts) {
				latest = { ...rec, id };
			}
		}
	}

	return latest;
}
